export const SECTION_AUDIO_CONTROLS_LIST = 'nodownload noplaybackrate noremoteplayback'

const AUDIO_PHRASE_ATTR = 'data-audio-phrase'
const SECTION_AUDIO_ATTR = 'data-section-audio'

export type MarkdownAudioAttachmentType = 'phrase' | 'section'

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function buildAudioPhraseHtml({
  phrase,
  audioUrl,
  hint,
}: {
  phrase: string
  audioUrl: string
  hint?: string
}): string {
  const trimmedPhrase = phrase.trim()
  const trimmedUrl = audioUrl.trim()
  if (!trimmedPhrase || !trimmedUrl) {
    return trimmedPhrase
  }

  const hintAttr = hint?.trim()
    ? ` data-audio-hint="${escapeHtmlAttr(hint.trim())}"`
    : ''

  return `<span ${AUDIO_PHRASE_ATTR} data-audio-url="${escapeHtmlAttr(trimmedUrl)}"${hintAttr}>${trimmedPhrase}</span>`
}

export function buildSectionAudioHtml({ audioUrl }: { audioUrl: string }): string {
  const trimmedUrl = audioUrl.trim()
  if (!trimmedUrl) return ''

  const escapedUrl = escapeHtmlAttr(trimmedUrl)

  return `<div ${SECTION_AUDIO_ATTR} data-audio-url="${escapedUrl}" class="not-prose heading-audio-row"><audio controls controlslist="${SECTION_AUDIO_CONTROLS_LIST}" preload="none" class="heading-inline-audio" src="${escapedUrl}"></audio></div>`
}

export function isAudioPhraseNode(properties: Record<string, unknown> | undefined): boolean {
  if (!properties) return false
  return (
    properties[AUDIO_PHRASE_ATTR] !== undefined ||
    properties.dataAudioPhrase !== undefined ||
    properties['data-audio-phrase'] !== undefined
  )
}

export function getAudioPhraseUrl(properties: Record<string, unknown> | undefined): string {
  if (!properties) return ''
  const value =
    properties['data-audio-url'] ??
    properties.dataAudioUrl ??
    properties['dataAudioUrl']
  return typeof value === 'string' ? value : ''
}

export function getAudioPhraseHint(properties: Record<string, unknown> | undefined): string {
  if (!properties) return ''
  const value =
    properties['data-audio-hint'] ??
    properties.dataAudioHint ??
    properties['dataAudioHint']
  return typeof value === 'string' ? value : ''
}

export interface HastLikeNode {
  tagName?: string
  properties?: Record<string, unknown>
  children?: HastLikeNode[]
}

function getClassNames(properties: Record<string, unknown> | undefined): string[] {
  if (!properties) return []
  const value = properties.className ?? properties.class
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string') return value.split(/\s+/).filter(Boolean)
  return []
}

export function isSectionAudioRow(properties: Record<string, unknown> | undefined): boolean {
  if (!properties) return false
  return (
    getClassNames(properties).includes('heading-audio-row') ||
    properties[SECTION_AUDIO_ATTR] !== undefined ||
    properties.dataSectionAudio !== undefined ||
    properties['data-section-audio'] !== undefined
  )
}

export function isHeadingInlineAudio(properties: Record<string, unknown> | undefined): boolean {
  return getClassNames(properties).includes('heading-inline-audio')
}

export function getSectionAudioUrlFromNode(node: HastLikeNode | undefined): string {
  const fromAttributes = getAudioPhraseUrl(node?.properties)
  if (fromAttributes) return fromAttributes

  const audioChild = node?.children?.find(child => child.tagName === 'audio')
  if (!audioChild?.properties) return ''

  const value = audioChild.properties.src ?? audioChild.properties['data-audio-url'] ?? audioChild.properties.dataAudioUrl
  return typeof value === 'string' ? value : ''
}

export function headingHasSectionAudio(node: HastLikeNode | null | undefined): boolean {
  return Boolean(
    node?.children?.some(child => child.tagName === 'div' && isSectionAudioRow(child.properties))
  )
}

const SECTION_AUDIO_HTML_PATTERN =
  /<div[^>]*(?:data-section-audio|heading-audio-row)[^>]*>[\s\S]*?<\/div>/gi

export function stripSectionAudioFromHeadingText(text: string): string {
  return text.replace(SECTION_AUDIO_HTML_PATTERN, '').replace(/\s{2,}/g, ' ').trim()
}
